import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('notes_token') || '';

async function fetchWithAuth(url, options = {}) {
  const token = getToken();
  const res = await fetch(`${API_URL}${url}`, { ...options, headers: { ...(options.headers || {}), 'x-token': token } });
  if (res.status === 401) { localStorage.removeItem('notes_token'); localStorage.removeItem('notes_user'); alert('登录超时，请重新登录'); window.location.href = '/'; return null; }
  return res;
}

export default function TaskOverview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [filterType, setFilterType] = useState(''); // 'thisWeek', 'thisMonth', 'custom'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    loadProject();
    loadData();
  }, [id]);

  const loadProject = async () => {
    try {
      const res = await fetchWithAuth(`/api/projects/${id}`);
      if (res) {
        const data = await res.json();
        setProject(data);
      }
    } catch (e) { console.error(e); }
  };

  const loadData = async (ft, df, dt) => {
    setLoading(true);
    try {
      let url = `/api/projects/${id}/task-overview`;
      const params = [];
      const fType = ft !== undefined ? ft : filterType;
      if (fType === 'thisWeek' || fType === 'thisMonth') {
        const range = fType === 'thisWeek' ? getWeekRange() : getMonthRange();
        params.push(`dateFrom=${range.start}`);
        params.push(`dateTo=${range.end}`);
      } else if (fType === 'custom') {
        const from = df !== undefined ? df : dateFrom;
        const to = dt !== undefined ? dt : dateTo;
        if (from) params.push(`dateFrom=${from}`);
        if (to) params.push(`dateTo=${to}`);
      }
      if (params.length > 0) url += '?' + params.join('&');
      
      const res = await fetchWithAuth(url);
      if (!res) return;
      const data = await res.json();
      if (data.success) {
        setRows(data.rows || []);
        setAttachments(data.attachments || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const getWeekRange = () => {
    const now = new Date();
    const day = now.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMonday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return {
      start: monday.toISOString().split('T')[0],
      end: sunday.toISOString().split('T')[0]
    };
  };

  const getMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  // 清除条件：重新加载全部数据（Bug 2 修复）
  const clearFilter = () => {
    setFilterType('');
    loadData('', '', '');
  };

  const doFilter = (ft) => {
    setFilterType(ft);
    if (ft === 'thisWeek' || ft === 'thisMonth') {
      loadData(ft, '', '');
    } else if (ft === '') {
      loadData('', '', '');
    }
  };

  const doCustomFilter = () => {
    loadData('custom', dateFrom, dateTo);
  };

  const exportExcel = async () => {
    try {
      let url = `${API_URL}/api/projects/${id}/task-overview/export`;
      const params = [];
      if (filterType === 'thisWeek' || filterType === 'thisMonth') {
        const range = filterType === 'thisWeek' ? getWeekRange() : getMonthRange();
        params.push(`dateFrom=${range.start}`);
        params.push(`dateTo=${range.end}`);
      } else if (filterType === 'custom') {
        if (dateFrom) params.push(`dateFrom=${dateFrom}`);
        if (dateTo) params.push(`dateTo=${dateTo}`);
      }
      if (params.length > 0) url += '?' + params.join('&');
      
      // 使用 fetchWithAuth 获取 blob 再下载（带认证）
      const res = await fetchWithAuth(url);
      if (!res || !res.ok) { alert('导出失败'); return; }
      const blob = await res.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `任务概览_${new Date().toLocaleDateString('zh-CN').replace(/\//g, '-')}.xlsx`;
      a.click();
      URL.revokeObjectURL(downloadUrl);
    } catch (e) { alert('导出失败: ' + e.message); }
  };

  // 附件去重（Feature 2 修复）
  const uniqueAttachments = useMemo(() => {
    return [...new Map(attachments.map(a => [a.originalName || a.original_name || a.filename, a])).values()];
  }, [attachments]);

  // 计算渲染数据：为每行生成合并单元格标记（Bug 3 修复）
  const renderData = useMemo(() => {
    const result = [];
    
    // 计算level1的span
    const level1SpanMap = {};
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].level === 1) {
        let span = 1;
        for (let j = i + 1; j < rows.length; j++) {
          if (rows[j].level === 1) break;
          span++;
        }
        level1SpanMap[i] = span;
      }
    }
    
    // 计算level2的span
    const level2SpanMap = {};
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].level === 2) {
        let span = 1;
        for (let j = i + 1; j < rows.length; j++) {
          if (rows[j].level <= 2) break;
          span++;
        }
        level2SpanMap[i] = span;
      }
    }
    
    // 生成渲染数据
    let prevLevel1 = null, prevLevel2 = null;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const isL1 = row.level === 1;
      const isL2 = row.level === 2;
      const isL3 = row.level >= 3;
      
      const l1Span = level1SpanMap[i] || 0;
      const l2Span = level2SpanMap[i] || 0;
      
      // 检测"备注行"：当前是一级/二级任务，有备注，且下一条是同级的子任务
      let isRemarksRow = false;
      if ((isL1 || isL2) && row.remarks) {
        const nextRow = i + 1 < rows.length ? rows[i + 1] : null;
        if (nextRow && nextRow.level > row.level) {
          isRemarksRow = true;
        }
      }
      
      result.push({
        ...row,
        _level1Render: isL1 && l1Span > 0,
        _level1Span: isL1 ? l1Span : 0,
        _level2Render: isL2 && l2Span > 0,
        _level2Span: isL2 ? l2Span : 0,
        _isL3: isL3,
        _isRemarksRow: isRemarksRow,
        _remarksColSpan: isRemarksRow ? 2 : 1, // 合并二级+详细任务列
      });
    }
    
    return result;
  }, [rows]);

  if (loading) return <div className="loading-state">加载中...</div>;

  return (
    <div className="page-container">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn-secondary" onClick={() => navigate(`/projects/${id}`)} style={{ padding: '4px 12px' }}>← 返回项目</button>
          <h2 style={{ margin: 0, fontSize: 20 }}>{project?.name} - 任务概览</h2>
        </div>
        <button 
          onClick={exportExcel}
          style={{ padding: '8px 16px', background: '#52c41a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
        >
          📥 导出Excel
        </button>
      </div>

      {/* 筛选栏 */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'center', flexWrap: 'wrap', padding: 16, background: '#fafafa', borderRadius: 8 }}>
        <span style={{ fontSize: 14, color: '#666' }}>筛选：</span>
        <button 
          onClick={() => doFilter('thisWeek')}
          style={{ 
            padding: '6px 16px', borderRadius: 6, border: filterType === 'thisWeek' ? '1px solid #1890ff' : '1px solid #d9d9d9', 
            background: filterType === 'thisWeek' ? '#e6f7ff' : '#fff', 
            color: filterType === 'thisWeek' ? '#1890ff' : '#333',
            cursor: 'pointer', fontWeight: filterType === 'thisWeek' ? 600 : 400
          }}
        >
          📅 本周
        </button>
        <button 
          onClick={() => doFilter('thisMonth')}
          style={{ 
            padding: '6px 16px', borderRadius: 6, border: filterType === 'thisMonth' ? '1px solid #1890ff' : '1px solid #d9d9d9', 
            background: filterType === 'thisMonth' ? '#e6f7ff' : '#fff', 
            color: filterType === 'thisMonth' ? '#1890ff' : '#333',
            cursor: 'pointer', fontWeight: filterType === 'thisMonth' ? 600 : 400
          }}
        >
          📆 本月
        </button>
        <button 
          onClick={() => doFilter('custom')}
          style={{ 
            padding: '6px 16px', borderRadius: 6, border: filterType === 'custom' ? '1px solid #1890ff' : '1px solid #d9d9d9', 
            background: filterType === 'custom' ? '#e6f7ff' : '#fff', 
            color: filterType === 'custom' ? '#1890ff' : '#333',
            cursor: 'pointer', fontWeight: filterType === 'custom' ? 600 : 400
          }}
        >
          📊 自定义日期
        </button>
        {filterType && (
          <button 
            onClick={clearFilter}
            style={{ padding: '6px 12px', fontSize: 12, borderRadius: 6, border: '1px solid #d9d9d9', background: '#fff', cursor: 'pointer', color: '#999' }}
          >
            清除筛选 ✕
          </button>
        )}
        {filterType === 'custom' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input 
              type="date" 
              value={dateFrom} 
              onChange={e => setDateFrom(e.target.value)}
              style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d9d9d9' }}
            />
            <span style={{ color: '#999' }}>至</span>
            <input 
              type="date" 
              value={dateTo} 
              onChange={e => setDateTo(e.target.value)}
              style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d9d9d9' }}
            />
            <button 
              onClick={doCustomFilter}
              style={{ padding: '6px 16px', background: '#1890ff', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
            >
              查询
            </button>
          </div>
        )}
      </div>

      {/* 任务表格 - Bug 3: 修复列错位 */}
      <div style={{ marginBottom: 32, overflowX: 'auto', background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#fafafa' }}>
              <th style={{ padding: '12px 16px', borderBottom: '2px solid #e8e8e8', textAlign: 'left', fontWeight: 600 }}>一级任务</th>
              <th style={{ padding: '12px 16px', borderBottom: '2px solid #e8e8e8', textAlign: 'left', fontWeight: 600 }}>二级任务</th>
              <th style={{ padding: '12px 16px', borderBottom: '2px solid #e8e8e8', textAlign: 'left', fontWeight: 600 }}>详细任务</th>
              <th style={{ padding: '12px 16px', borderBottom: '2px solid #e8e8e8', textAlign: 'left', fontWeight: 600 }}>任务描述(备注)</th>
              <th style={{ padding: '12px 16px', borderBottom: '2px solid #e8e8e8', textAlign: 'left', fontWeight: 600 }}>开始时间</th>
              <th style={{ padding: '12px 16px', borderBottom: '2px solid #e8e8e8', textAlign: 'left', fontWeight: 600 }}>计划完成时间</th>
              <th style={{ padding: '12px 16px', borderBottom: '2px solid #e8e8e8', textAlign: 'left', fontWeight: 600 }}>实际完成时间</th>
              <th style={{ padding: '12px 16px', borderBottom: '2px solid #e8e8e8', textAlign: 'left', fontWeight: 600 }}>执行备注(工作说明)</th>
            </tr>
                    </thead>
          <tbody>
            {renderData.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ padding: 40, textAlign: 'center', color: '#999' }}>暂无任务数据</td>
              </tr>
            ) : renderData.map((row, idx) => {
              const isL1 = row.level === 1;
              const isL2 = row.level === 2;
              const isL3 = row.level >= 3;
              
              // 场景1：只有一级任务，二级列应渲染空td（但非备注行）
              const showL2td = isL1 && !row._level2Render && !row._isRemarksRow; // 一级任务但无二级列
              
              return (
                <tr key={row.id || idx} style={{ borderBottom: '1px solid #f0f0f0', background: row._isRemarksRow ? '#fffbe6' : 'transparent' }}>
                  {/* Col 0: 一级任务 */}
                  {row._level1Render ? (
                    <td rowSpan={row._level1Span} style={{ 
                      padding: '10px 16px',
                      borderRight: '1px solid #f0f0f0',
                      verticalAlign: 'middle',
                      fontWeight: 600,
                      background: '#fafafa'
                    }}>
                      {row.level1}
                    </td>
                  ) : null}
                  
                  {/* Col 1: 二级任务 */}
                  {row._level2Render ? (
                    <td rowSpan={row._level2Span} style={{ 
                      padding: '10px 16px',
                      borderRight: '1px solid #f0f0f0',
                      verticalAlign: 'middle',
                      fontWeight: 500,
                      background: '#fafafa'
                    }}>
                      {row.level2}
                    </td>
                  ) : showL2td ? (
                    <td style={{ padding: '10px 16px', borderRight: '1px solid #f0f0f0' }}></td>
                  ) : null}
                  
                  {/* Col 2: 详细任务 */}
                  {/* 场景2：备注行，合并二级+详细任务列 */}
                  {row._isRemarksRow ? (
                    <td colSpan={row._remarksColSpan} style={{ 
                      padding: '10px 16px', 
                      borderRight: '1px solid #f0f0f0',
                      background: '#fffbe6',
                      fontWeight: 500
                    }}>
                      阶段/任务整体概述
                    </td>
                  ) : (
                    <td style={{ padding: '10px 16px', borderRight: '1px solid #f0f0f0' }}>
                      {isL3 ? row.detail : (isL2 && !row._level2Render) ? row.level2 : ''}
                    </td>
                  )}
                  
                  {/* Col 3-7: 始终渲染 */}
                  <td style={{ padding: '10px 16px', color: '#666', fontSize: 13 }}>{row.remarks}</td>
                  <td style={{ padding: '10px 16px', color: '#666', fontSize: 13 }}>{row.plannedStart}</td>
                  <td style={{ 
                    padding: '10px 16px', 
                    fontSize: 13,
                    color: (row.plannedEnd && !row.actualEnd && new Date(row.plannedEnd) < new Date(new Date().toISOString().split('T')[0])) ? '#ff4d4f' : '#666',
                    fontWeight: (row.plannedEnd && !row.actualEnd && new Date(row.plannedEnd) < new Date(new Date().toISOString().split('T')[0])) ? 600 : 400
                  }}>
                    {row.plannedEnd}
                    {(row.plannedEnd && !row.actualEnd && new Date(row.plannedEnd) < new Date(new Date().toISOString().split('T')[0])) && ' ⚠️'}
                  </td>
                  <td style={{ 
                    padding: '10px 16px',
                    color: row.actualEnd ? '#52c41a' : '#999',
                    fontSize: 13
                  }}>{row.actualEnd || '-'}</td>
                  <td style={{ padding: '10px 16px', color: '#666', fontSize: 13, maxWidth: 200 }}>{row.workNotes}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 附件列表 - Bug 4: 确保filter变化时重新加载，Feature 2: 去重显示 */}
      {uniqueAttachments.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, padding: 20 }}>
          <h3 style={{ fontSize: 16, margin: '0 0 16px 0' }}>📎 附件列表（共 {uniqueAttachments.length} 个{attachments.length > uniqueAttachments.length ? `，去重前${attachments.length}个` : ''}）</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {uniqueAttachments.map((att, idx) => (
              <div key={att.id || idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: '#fafafa', borderRadius: 6 }}>
                <span style={{ color: '#1890ff' }}>📄</span>
                <span style={{ flex: 1 }}>{att.originalName || att.original_name || att.filename}</span>
                <span style={{ color: '#666', fontSize: 12 }}>{att.taskName}</span>
                {att.assignee && <span style={{ color: '#999', fontSize: 12 }}>{att.assignee}</span>}
                {att.period && <span style={{ padding: '2px 6px', borderRadius: 4, background: '#e6f7ff', color: '#1890ff', fontSize: 11 }}>第{att.period}期</span>}
                {att.version && <span style={{ padding: '2px 6px', borderRadius: 4, background: '#f9f0ff', color: '#722ed1', fontSize: 11 }}>版本{att.version}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
